import { sprintStatusLabels, type Sprint, type Task } from '@nexo/types';
import { Badge, Button, IconButton, cn, useToast } from '@nexo/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, Flag, KeyRound, Pencil, Play, Plus, Trash2 } from 'lucide-react';
import { useState, type DragEvent, type ReactNode, type SubmitEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useTeam } from '../../layouts/TeamContext';
import { api } from '../../lib/api';
import { can } from '../../lib/permissions';
import { keys, useSprints } from '../../lib/queries';
import {
  CompleteSprintDialog,
  SprintFormDialog,
  StartSprintDialog,
  TaskKeyDialog,
} from './SprintDialogs';
import { averageVelocity, pointsOf, sprintDates } from './sprintUtils';
import { TaskRow } from '../tasks/TaskRow';
import { useCreateTask, useUpdateTask } from '../tasks/useTaskMutations';
import { errorMessage } from '../../lib/forms';

const BACKLOG = 'backlog';

type Dialog =
  | { kind: 'create' }
  | { kind: 'edit' | 'start' | 'complete'; sprint: Sprint }
  | { kind: 'key' }
  | null;

/**
 * Jira-style planning: the active sprint, then planned sprints, then the backlog (tasks in no
 * sprint). Tasks are dragged between sections to plan them.
 */
export function BacklogView({ tasks }: { tasks: Task[] }) {
  const { t } = useTranslation();
  const { team } = useTeam();
  const sprints = useSprints(team.id);
  const update = useUpdateTask();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [dialog, setDialog] = useState<Dialog>(null);
  const manage = can(team, 'manage_tasks');

  const open = (sprints.data ?? []).filter((s) => s.status !== 'completed');
  const active = open.find((s) => s.status === 'active');
  const planned = open.filter((s) => s.status === 'planned');
  const inSprint = (id: string) => tasks.filter((task) => task.sprintId === id);
  // Suggested capacity for planned sprints: what the team usually finishes.
  const velocity = averageVelocity(sprints.data ?? []);
  // Finished tasks in no sprint are history, not backlog.
  const openIds = new Set(open.map((s) => s.id));
  const backlog = tasks.filter(
    (task) => (!task.sprintId || !openIds.has(task.sprintId)) && task.status !== 'done',
  );

  const remove = useMutation({
    mutationFn: (sprint: Sprint) => api.sprints.remove(sprint.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.sprints(team.id) });
      void queryClient.invalidateQueries({ queryKey: keys.tasks(team.id) });
    },
    onError: (err) =>
      toast({
        title: t('No se pudo borrar el sprint'),
        description: errorMessage(err),
        tone: 'error',
      }),
  });

  function moveTask(taskId: string, target: string) {
    const task = tasks.find((task) => task.id === taskId);
    const sprintId = target === BACKLOG ? null : target;
    if (task && task.sprintId !== sprintId) update.mutate({ id: taskId, input: { sprintId } });
  }

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-4 px-3 py-4 sm:px-6">
      <div className="flex flex-wrap items-center gap-2">
        <p className="mr-auto text-sm text-muted">
          {t('Arrastra las tareas entre el backlog y los sprints para planificarlas.')}
        </p>
        {can(team, 'manage_team') && (
          <Button
            size="sm"
            variant="ghost"
            icon={<KeyRound className="size-4" />}
            onClick={() => setDialog({ kind: 'key' })}
          >
            {t('Clave {{key}}', { key: team.taskKey })}
          </Button>
        )}
        {manage && (
          <Button
            size="sm"
            variant="secondary"
            icon={<Plus className="size-4" />}
            onClick={() => setDialog({ kind: 'create' })}
          >
            {t('Crear sprint')}
          </Button>
        )}
      </div>

      {[...(active ? [active] : []), ...planned].map((sprint) => {
        const list = inSprint(sprint.id);
        const done = list.filter((task) => task.status === 'done');
        return (
          <Section
            key={sprint.id}
            id={sprint.id}
            title={sprint.name}
            badge={
              <Badge tone={sprint.status === 'active' ? 'accent' : 'neutral'}>
                {t(sprintStatusLabels[sprint.status])}
              </Badge>
            }
            subtitle={[
              sprintDates(sprint),
              list.length === 1 ? t('1 tarea') : t('{{count}} tareas', { count: list.length }),
              sprint.status === 'active'
                ? t('{{done}}/{{total}} puntos hechos', {
                    done: pointsOf(done),
                    total: pointsOf(list),
                  })
                : t('{{count}} puntos planificados', { count: pointsOf(list) }),
            ]
              .filter(Boolean)
              .join(' · ')}
            note={
              sprint.status === 'planned' &&
              velocity !== null && (
                <span
                  className={cn(
                    'text-xs',
                    pointsOf(list) > velocity ? 'text-warning' : 'text-subtle',
                  )}
                >
                  {pointsOf(list) > velocity
                    ? t('Supera la velocidad media ({{velocity}} pts)', { velocity })
                    : t('Capacidad sugerida: {{velocity}} pts', { velocity })}
                </span>
              )
            }
            goal={sprint.goal}
            tasks={list}
            onDropTask={moveTask}
            actions={
              manage && (
                <>
                  {sprint.status === 'active' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Flag className="size-4" />}
                      onClick={() => setDialog({ kind: 'complete', sprint })}
                    >
                      {t('Completar sprint')}
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      icon={<Play className="size-4" />}
                      disabled={active !== undefined}
                      title={
                        active ? t('Completa antes {{name}}', { name: active.name }) : undefined
                      }
                      onClick={() => setDialog({ kind: 'start', sprint })}
                    >
                      {t('Iniciar sprint')}
                    </Button>
                  )}
                  <IconButton
                    label={t('Editar sprint')}
                    size="sm"
                    onClick={() => setDialog({ kind: 'edit', sprint })}
                  >
                    <Pencil />
                  </IconButton>
                  <IconButton
                    label={t('Borrar sprint')}
                    size="sm"
                    className="hover:text-danger"
                    onClick={() => {
                      const question =
                        list.length > 0
                          ? t('¿Borrar {{name}}? Sus tareas volverán al backlog.', {
                              name: sprint.name,
                            })
                          : t('¿Borrar {{name}}?', { name: sprint.name });
                      if (window.confirm(question)) remove.mutate(sprint);
                    }}
                  >
                    <Trash2 />
                  </IconButton>
                </>
              )
            }
          />
        );
      })}

      <Section
        id={BACKLOG}
        title={t('Backlog')}
        subtitle={`${
          backlog.length === 1 ? t('1 tarea') : t('{{count}} tareas', { count: backlog.length })
        } · ${
          pointsOf(backlog) === 1
            ? t('1 punto')
            : t('{{count}} puntos', { count: pointsOf(backlog) })
        }`}
        tasks={backlog}
        onDropTask={moveTask}
        emptyText={
          open.length === 0
            ? t('El backlog está vacío. Crea tareas aquí y un sprint para planificarlas.')
            : t('El backlog está vacío.')
        }
      />

      {dialog?.kind === 'create' && (
        <SprintFormDialog
          open
          defaultName={t('Sprint {{n}}', { n: (sprints.data?.length ?? 0) + 1 })}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'edit' && (
        <SprintFormDialog open sprint={dialog.sprint} onClose={() => setDialog(null)} />
      )}
      {dialog?.kind === 'start' && (
        <StartSprintDialog
          open
          sprint={dialog.sprint}
          tasks={inSprint(dialog.sprint.id)}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'complete' && (
        <CompleteSprintDialog
          open
          sprint={dialog.sprint}
          tasks={inSprint(dialog.sprint.id)}
          planned={planned}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.kind === 'key' && <TaskKeyDialog open onClose={() => setDialog(null)} />}
    </div>
  );
}

function Section({
  id,
  title,
  badge,
  subtitle,
  note,
  goal,
  tasks,
  actions,
  emptyText,
  onDropTask,
}: {
  id: string;
  title: string;
  badge?: ReactNode;
  subtitle: string;
  /** Extra hint next to the subtitle (e.g. suggested capacity). */
  note?: ReactNode;
  goal?: string | null;
  tasks: Task[];
  actions?: ReactNode;
  emptyText?: string;
  onDropTask: (taskId: string, target: string) => void;
}) {
  const { t } = useTranslation();
  const [collapsed, setCollapsed] = useState(false);
  const [over, setOver] = useState(false);

  function onDrop(event: DragEvent) {
    event.preventDefault();
    setOver(false);
    const taskId = event.dataTransfer.getData('text/task-id');
    if (taskId) onDropTask(taskId, id);
  }

  return (
    <section
      aria-label={title}
      onDragOver={(e) => {
        e.preventDefault();
        setOver(true);
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(false);
      }}
      onDrop={onDrop}
      className={cn(
        'rounded-2xl bg-surface-2/50 p-2 ring-1 ring-inset ring-border transition-colors',
        over && 'bg-accent-soft ring-accent/40',
      )}
    >
      <header className="flex flex-wrap items-center gap-x-2 gap-y-1 px-1.5 py-1">
        <button
          type="button"
          aria-expanded={!collapsed}
          onClick={() => setCollapsed((v) => !v)}
          className="flex min-w-0 items-center gap-1.5 rounded-md py-1 pr-1 text-left"
        >
          <ChevronRight
            className={cn(
              'size-4 shrink-0 text-subtle transition-transform',
              !collapsed && 'rotate-90',
            )}
          />
          <h2 className="truncate text-sm font-semibold">{title}</h2>
        </button>
        {badge}
        <span className="text-xs text-subtle">{subtitle}</span>
        {note}
        <span className="ml-auto flex items-center gap-1">{actions}</span>
      </header>
      {goal && !collapsed && <p className="px-8 pb-1 text-[13px] text-muted">{goal}</p>}
      {!collapsed && (
        <div className="mt-1 flex flex-col">
          {tasks.map((task) => (
            <div
              key={task.id}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData('text/task-id', task.id);
                e.dataTransfer.effectAllowed = 'move';
              }}
              className="cursor-grab rounded-lg bg-surface active:cursor-grabbing [&+&]:mt-px"
            >
              <TaskRow task={task} />
            </div>
          ))}
          {tasks.length === 0 && (
            <p className="rounded-xl border border-dashed border-border-strong/60 px-3 py-5 text-center text-xs text-subtle">
              {emptyText ?? t('Arrastra tareas aquí para planificarlas en este sprint.')}
            </p>
          )}
          <QuickAdd sprintId={id === BACKLOG ? null : id} />
        </div>
      )}
    </section>
  );
}

function QuickAdd({ sprintId }: { sprintId: string | null }) {
  const { t } = useTranslation();
  const create = useCreateTask();
  const [title, setTitle] = useState('');

  function onSubmit(event: SubmitEvent) {
    event.preventDefault();
    const value = title.trim();
    if (!value) return;
    create.mutate({ title: value, sprintId });
    setTitle('');
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-1 flex items-center gap-3 rounded-lg px-2.5 py-1.5 focus-within:bg-surface"
    >
      <Plus className="size-4 text-subtle" />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('Crear tarea y pulsar Enter')}
        aria-label={sprintId ? t('Nueva tarea en el sprint') : t('Nueva tarea en el backlog')}
        maxLength={200}
        className="min-w-0 flex-1 bg-transparent text-sm placeholder:text-subtle focus:outline-none"
      />
    </form>
  );
}
